import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar, Box, Button, CircularProgress, InputBase } from '@mui/material';
import { Search } from '@mui/icons-material';
import { formatInTimeZone } from 'date-fns-tz';
import { bookingStatusConfig } from '../../utils/bookingStatus';
import { classColorTokens } from '../../utils/classColors';
import BookingStatusDialog from '../BookingStatusDialog';
import { bookingSubtitle, initials } from './SessionBookingsDrawer';
import type { DrawerBooking } from './SessionBookingsDrawer';
import type { ScheduleSession } from './ScheduleCalendarView';
import { DueChip, MemberNoteIcon, TrialTag, dueLabel, isPaymentPending } from './bookingRowBits';
import type { BookingNotes } from './bookingRowBits';
import BookingQuickActions from './BookingQuickActions';
import BookingStatusPill from './BookingStatusPill';
import MemberDetailDialog from './MemberDetailDialog';
import { SCHED, avatarTint, btnSx, btnSmSx } from './scheduleTheme';

const IST_TIMEZONE = 'Asia/Kolkata';

const escapeHtml = (value: string) =>
  value.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);

// Member names are free text: a leading =, +, - or @ would run as a spreadsheet formula.
const csvCell = (value: string) => `"${(/^[=+\-@]/.test(value) ? `'${value}` : value).replace(/"/g, '""')}"`;

const sessionTime = (s: ScheduleSession) => formatInTimeZone(new Date(s.date), IST_TIMEZONE, 'h:mm a').toUpperCase();

interface ScheduleTodayListProps {
  dayKey: string;
  isToday: boolean;
  sessions: ScheduleSession[];
  bookingsBySession: Record<string, DrawerBooking[]>;
  waitingBySession: Record<string, number> | null;
  loading: boolean;
  now: Date;
  canDownload: boolean;
  notes: BookingNotes;
  colorOf: (s: ScheduleSession) => string;
  onOpenSession: (id: string) => void;
  onBookingUpdated: (bookingId: string, status: string) => void;
  /** Moved or paid from a row: the list refetches. */
  onBookingsChanged: () => void;
  onError: (message: string) => void;
}

const ScheduleTodayList: React.FC<ScheduleTodayListProps> = ({
  dayKey,
  isToday,
  sessions,
  bookingsBySession,
  waitingBySession,
  loading,
  now,
  canDownload,
  notes,
  colorOf,
  onOpenSession,
  onBookingUpdated,
  onBookingsChanged,
  onError,
}) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [statusTarget, setStatusTarget] = useState<{ booking: DrawerBooking; initialStatus?: string } | null>(null);
  const [memberTarget, setMemberTarget] = useState<{ user: NonNullable<DrawerBooking['user']>; bookingId: string; instructorName?: string } | null>(null);

  // Cancelled classes and cancelled bookings are nobody the front desk is expecting.
  const groups = useMemo(
    () =>
      sessions
        .filter((s) => s.isLive !== false)
        .map((s) => ({
          session: s,
          rows: (bookingsBySession[s._id] || []).filter((b) => b.bookingStatus !== 'CANCELLED'),
        }))
        .filter((g) => g.rows.length > 0),
    [sessions, bookingsBySession],
  );

  const visibleGroups = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return groups;
    const digits = q.replace(/\D/g, '');
    return groups
      .map((g) => ({
        ...g,
        rows: g.rows.filter(
          (b) =>
            (b.user?.name || '').toLowerCase().includes(q) ||
            (digits.length > 0 && (b.user?.phoneNumber || '').replace(/\D/g, '').includes(digits)),
        ),
      }))
      .filter((g) => g.rows.length > 0);
  }, [groups, query]);

  const spotsOf = (list: DrawerBooking[]) => list.reduce((n, b) => n + (b.quantity ?? 1), 0);
  const allRows = groups.flatMap((g) => g.rows);
  const bookedTotal = spotsOf(allRows);
  const checkedInTotal = spotsOf(allRows.filter((b) => b.bookingStatus === 'CHECKED_IN'));
  const pendingTotal = allRows.filter(isPaymentPending).length;
  const waitingTotal = waitingBySession
    ? sessions.filter((s) => s.isLive !== false).reduce((n, s) => n + (waitingBySession[s._id] || 0), 0)
    : null;

  const dayWord = isToday ? 'Today' : formatInTimeZone(new Date(`${dayKey}T00:00:00Z`), 'UTC', 'EEE d MMM');

  const handlePrint = () => {
    const win = window.open('', '_blank');
    if (!win) {
      onError('Allow pop-ups to print the sign-in sheet');
      return;
    }
    const body = groups
      .map(
        ({ session: s, rows }) => `
        <h2>${escapeHtml(`${sessionTime(s)} · ${s.className}`)}${s.instructor?.name ? ` <small>With ${escapeHtml(s.instructor.name)}</small>` : ''}</h2>
        <table>
          <thead><tr><th>Name</th><th>Phone</th><th>Pack</th><th class="sign">Sign</th></tr></thead>
          <tbody>${rows
            .map(
              (b) =>
                `<tr><td>${escapeHtml(b.user?.name || 'Unknown member')}</td><td>${escapeHtml(b.user?.phoneNumber || '')}</td><td>${escapeHtml(bookingSubtitle(b))}${isPaymentPending(b) ? ` · <strong>${dueLabel(b)}</strong>` : ''}</td><td></td></tr>`,
            )
            .join('')}</tbody>
        </table>`,
      )
      .join('');
    const title = `Sign-in sheet, ${formatInTimeZone(new Date(`${dayKey}T00:00:00Z`), 'UTC', 'EEE d MMM yyyy')}`;
    win.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(title)}</title><style>
      body { font-family: "DM Sans", system-ui, sans-serif; color: #2B2B2E; margin: 24px; }
      h1 { font-size: 20px; margin: 0 0 16px; }
      h2 { font-size: 15px; margin: 20px 0 8px; }
      small { font-weight: 400; color: #66666D; }
      table { width: 100%; border-collapse: collapse; font-size: 13px; page-break-inside: avoid; }
      th, td { border: 1px solid #D9D3D5; padding: 8px; text-align: left; }
      th.sign { width: 22%; }
    </style></head><body><h1>${escapeHtml(title)}</h1>${body}</body></html>`);
    win.document.close();
    win.focus();
    win.print();
  };

  const handleDownload = () => {
    const header = ['Time', 'Class', 'Instructor', 'Member', 'Phone', 'Pack', 'Status', 'Payment'];
    const lines = groups.flatMap(({ session: s, rows }) =>
      rows.map((b) =>
        [
          sessionTime(s),
          s.className,
          s.instructor?.name || '',
          b.user?.name || 'Unknown member',
          (b.user?.phoneNumber || '').replace(/\D/g, ''),
          bookingSubtitle(b),
          bookingStatusConfig(b.bookingStatus).label,
          isPaymentPending(b) ? dueLabel(b) : '',
        ]
          .map(csvCell)
          .join(','),
      ),
    );
    const blob = new Blob([[header.join(','), ...lines].join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bookings-${dayKey}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const count = (value: number | string, label: string) => (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'baseline', gap: '6px', fontSize: '14px', color: SCHED.muted, whiteSpace: 'nowrap' }}>
      <Box component="span" sx={{ fontSize: '20px', fontWeight: 700, color: SCHED.ink }}>{value}</Box>
      {label}
    </Box>
  );

  const renderAction = (b: DrawerBooking) => {
    if (b.bookingStatus === 'CHECKED_IN') {
      return (
        <Box
          component="button"
          type="button"
          aria-label="Checked In, change status"
          onClick={() => setStatusTarget({ booking: b })}
          sx={{ ...actionSx, backgroundColor: SCHED.success, color: '#FFFFFF', border: `1px solid ${SCHED.success}`, '&:hover': { backgroundColor: SCHED.successText } }}
        >
          {bookingStatusConfig('CHECKED_IN').label}
        </Box>
      );
    }
    if (b.bookingStatus === 'BOOKED') {
      return (
        <Box
          component="button"
          type="button"
          onClick={() => setStatusTarget({ booking: b, initialStatus: 'CHECKED_IN' })}
          sx={{ ...actionSx, backgroundColor: '#FFFFFF', color: SCHED.success, border: `1px solid ${SCHED.success}`, '&:hover': { backgroundColor: SCHED.successBg } }}
        >
          Check in
        </Box>
      );
    }
    return <BookingStatusPill status={b.bookingStatus} onClick={() => setStatusTarget({ booking: b })} />;
  };

  return (
    <Box sx={{ backgroundColor: '#FFFFFF', border: `1px solid ${SCHED.border}`, borderRadius: '20px', p: { xs: '16px', md: '20px 24px' } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap', mb: '16px' }}>
        <Box component="h2" sx={{ m: 0, fontSize: '22px', fontWeight: 700 }}>{dayWord}: everyone booked</Box>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '16px', flexWrap: 'wrap' }}>
          {count(bookedTotal, 'booked')}
          {count(checkedInTotal, 'checked in')}
          {waitingTotal != null && count(waitingTotal, 'waiting')}
          {pendingTotal > 0 && count(pendingTotal, 'due')}
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', height: 44, width: { xs: '100%', sm: 240 }, px: '12px', border: `1px solid ${SCHED.control}`, borderRadius: '12px', boxSizing: 'border-box' }}>
          <Search sx={{ fontSize: 20, color: SCHED.muted }} />
          <InputBase fullWidth placeholder="Find someone" value={query} onChange={(e) => setQuery(e.target.value)} sx={{ fontFamily: SCHED.font, fontSize: '14px' }} />
        </Box>
        <Button onClick={handlePrint} disabled={groups.length === 0} sx={btnSx}>Print sign-in sheet</Button>
        {canDownload && <Button onClick={handleDownload} disabled={groups.length === 0} sx={btnSx}>Download</Button>}
      </Box>

      {loading && groups.length === 0 ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={28} sx={{ color: SCHED.muted }} />
        </Box>
      ) : visibleGroups.length === 0 ? (
        <Box sx={{ py: '24px', textAlign: 'center', fontSize: '14px', color: SCHED.muted }}>
          {query.trim() ? 'No one by that name or number.' : 'No bookings on this day.'}
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {visibleGroups.map(({ session: s, rows }) => {
            const t = classColorTokens(colorOf(s));
            const end = new Date(new Date(s.date).getTime() + (s.duration || 0) * 60000);
            const booked = Math.max(0, s.slots - (s.availableSlots ?? s.slots));
            const sub = [
              s.instructor?.name ? `With ${s.instructor.name}` : null,
              s.classType?.title || s.classType?.name,
              `${booked}/${s.slots} booked`,
              end < now ? 'done' : null,
            ]
              .filter(Boolean)
              .join(' · ');
            const waiting = end < now ? 0 : waitingBySession?.[s._id] || 0;
            return (
              <Box key={s._id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', p: '10px 16px', borderRadius: '12px', backgroundColor: t.bg }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: t.accent, flexShrink: 0 }} />
                  <Box component="span" sx={{ fontSize: '15px', fontWeight: 700 }}>{sessionTime(s)} · {s.className}</Box>
                  <Box component="span" sx={{ fontSize: '13px', color: SCHED.inkSoft }}>{sub}</Box>
                  {waiting > 0 && (
                    <Box
                      component="button"
                      type="button"
                      onClick={() => navigate('/session-waitlist')}
                      sx={{ border: 0, p: '4px 9px', borderRadius: '999px', backgroundColor: SCHED.waitBg, color: SCHED.waitText, fontFamily: SCHED.font, fontSize: '12px', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}
                    >
                      {waiting} on waitlist
                    </Box>
                  )}
                  <Box
                    component="button"
                    type="button"
                    onClick={() => onOpenSession(s._id)}
                    sx={{ ml: 'auto', border: 0, p: 0, backgroundColor: 'transparent', fontFamily: SCHED.font, fontSize: '13px', fontWeight: 700, color: SCHED.primaryText, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                  >
                    Open class
                  </Box>
                </Box>
                {rows.map((b) => (
                  <Box
                    key={b._id}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: 'minmax(0, 1fr) auto', md: 'minmax(200px, 300px) 130px minmax(0, 1fr) auto' },
                      alignItems: 'center',
                      columnGap: '16px',
                      rowGap: '4px',
                      minHeight: 62,
                      boxSizing: 'border-box',
                      py: '8px',
                      px: '6px',
                      borderBottom: `1px solid ${SCHED.rowLine}`,
                      opacity: b.bookingStatus === 'NO_SHOW' ? 0.7 : 1,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px', minWidth: 0 }}>
                      <Avatar
                        src={b.user?.image}
                        sx={{ width: 38, height: 38, fontSize: '13px', fontWeight: 700, fontFamily: SCHED.font, backgroundColor: avatarTint(b.user?._id || b._id), color: SCHED.ink }}
                      >
                        {initials(b.user?.name)}
                      </Avatar>
                      <Box
                        component="button"
                        type="button"
                        disabled={!b.user?._id}
                        onClick={() => b.user?._id && setMemberTarget({ user: b.user, bookingId: b._id, instructorName: s.instructor?.name })}
                        sx={{ border: 0, p: 0, backgroundColor: 'transparent', fontFamily: SCHED.font, fontSize: '15px', fontWeight: 600, color: SCHED.ink, textAlign: 'left', cursor: b.user?._id ? 'pointer' : 'default', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', '&:hover:not(:disabled)': { textDecoration: 'underline' } }}
                      >
                        {b.user?.name || 'Unknown member'}
                      </Box>
                      <MemberNoteIcon notes={notes} booking={b} />
                    </Box>
                    <Box component="a" href={b.user?.phoneNumber ? `tel:${b.user.phoneNumber}` : undefined} sx={{ gridRow: { xs: 2, md: 'auto' }, gridColumn: { xs: '1 / -1', md: 'auto' }, fontSize: '13px', color: SCHED.muted, textDecoration: 'none', whiteSpace: 'nowrap', '&[href]:hover': { color: SCHED.ink, textDecoration: 'underline' } }}>
                      {b.user?.phoneNumber}
                    </Box>
                    <Box sx={{ gridRow: { xs: 3, md: 'auto' }, gridColumn: { xs: '1 / -1', md: 'auto' }, display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                      {b.bookingType === 'trial' && <TrialTag />}
                      {bookingSubtitle(b) !== 'Trial' && (
                        <Box component="span" sx={{ fontSize: '13px', color: SCHED.inkSoft, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {bookingSubtitle(b)}
                        </Box>
                      )}
                      {isPaymentPending(b) && <DueChip booking={b} />}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px' }}>
                      <BookingQuickActions booking={b} onChanged={onBookingsChanged} onError={onError} />
                      <Box sx={{ ml: '6px' }}>{renderAction(b)}</Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            );
          })}
        </Box>
      )}

      <BookingStatusDialog
        open={Boolean(statusTarget)}
        booking={statusTarget?.booking ?? null}
        initialStatus={statusTarget?.initialStatus}
        onClose={() => setStatusTarget(null)}
        onUpdated={(bookingId, newStatus) => {
          setStatusTarget(null);
          onBookingUpdated(bookingId, newStatus);
        }}
        onError={onError}
      />

      <MemberDetailDialog
        member={memberTarget?.user ?? null}
        bookingId={memberTarget?.bookingId}
        instructorName={memberTarget?.instructorName}
        onClose={() => setMemberTarget(null)}
      />
    </Box>
  );
};

const actionSx = {
  ...btnSmSx,
  minWidth: 104,
  borderRadius: '999px',
  fontFamily: SCHED.font,
  fontWeight: 700,
  whiteSpace: 'nowrap' as const,
  cursor: 'pointer',
  '&:focus-visible': { outline: `2px solid ${SCHED.ink}`, outlineOffset: 2 },
};

export default ScheduleTodayList;
